import React, { useMemo } from 'react';
import { Modal, Table, Badge, Spinner } from 'react-bootstrap';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';

import { GET_EVENT_REGISTRATIONS, GET_EVENT_PAYMENT_RECORDS } from 'helper/graphql/queries';

const formatPrice = (amount) =>
	amount > 0 ? `Ksh ${amount.toLocaleString()}` : null;

// Infer attendance mode from payment amount vs event prices
const inferMode = (amount, eventPrice, eventPhysicalPrice) => {
	if (!amount || amount === 0) return null;
	const onlyVirtual = eventPrice > 0 && !(eventPhysicalPrice > 0);
	const onlyPhysical = eventPhysicalPrice > 0 && !(eventPrice > 0);
	if (onlyVirtual) return 'virtual';
	if (onlyPhysical) return 'physical';
	if (amount === eventPrice && amount !== eventPhysicalPrice) return 'virtual';
	if (amount === eventPhysicalPrice && amount !== eventPrice) return 'physical';
	return 'paid'; // amounts equal or ambiguous — just confirm paid
};

const EventRegistrantsModal = ({ show, onHide, event }) => {
	const isPaidEvent = (event?.price > 0) || (event?.physicalPrice > 0);

	const { data, loading } = useQuery(GET_EVENT_REGISTRATIONS, {
		variables: { guid: event?.guid },
		skip: !show || !event?.guid,
		fetchPolicy: 'network-only',
	});

	const { data: paymentsData, loading: paymentsLoading } = useQuery(GET_EVENT_PAYMENT_RECORDS, {
		variables: { eventGuid: event?.guid },
		skip: !show || !event?.guid || !isPaidEvent,
		fetchPolicy: 'network-only',
	});

	const registrants = useMemo(() => {
		const eventNode = data?.allEvents?.edges?.[0]?.node;
		return eventNode?.registrations?.edges?.map((e) => e.node) || [];
	}, [data]);

	// Build a map of userGuid → payment record
	const paymentsByUser = useMemo(() => {
		const edges = paymentsData?.allPackagePayments?.edges || [];
		const map = {};
		edges.forEach(({ node }) => {
			if (node.user?.guid) map[node.user.guid] = node;
		});
		return map;
	}, [paymentsData]);

	const isLoading = loading || (isPaidEvent && paymentsLoading);
	const paidCount = isPaidEvent ? Object.keys(paymentsByUser).length : 0;

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton className="er-modal-header">
				<div>
					<Modal.Title className="er-modal-title">
						Registered Participants
					</Modal.Title>
					{event && (
						<p className="er-event-name mb-0">{event.title}</p>
					)}
				</div>
			</Modal.Header>

			<Modal.Body className="p-0">
				{isLoading && (
					<div className="er-loading">
						<Spinner animation="border" size="sm" className="me-2" />
						Loading registrants…
					</div>
				)}

				{!isLoading && registrants.length === 0 && (
					<div className="er-empty">
						<i className="fe fe-users" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
						<p className="mt-3 mb-0 text-muted">No registrations yet.</p>
					</div>
				)}

				{!isLoading && registrants.length > 0 && (
					<>
						<div className="er-count-bar">
							<Badge bg="" className="er-count-badge">
								<i className="fe fe-users me-1"></i>
								{registrants.length} participant{registrants.length !== 1 ? 's' : ''}
							</Badge>
							{isPaidEvent && (
								<Badge bg="" className="er-paid-badge">
									<i className="fe fe-credit-card me-1"></i>
									{paidCount} paid
								</Badge>
							)}
							{event?.maxParticipants > 0 && (
								<span className="er-capacity">
									Capacity: {event.maxParticipants}
								</span>
							)}
						</div>

						<div className="er-table-wrap">
							<Table hover className="er-table mb-0">
								<thead>
									<tr>
										<th>#</th>
										<th>Name</th>
										<th>Email</th>
										<th>Country</th>
										{isPaidEvent && <th>Payment</th>}
										<th>Registered At</th>
									</tr>
								</thead>
								<tbody>
									{registrants.map((r, idx) => {
										const payment = isPaidEvent
											? paymentsByUser[r.user?.guid]
											: null;
										const mode = payment
											? inferMode(payment.amount, event?.price, event?.physicalPrice)
											: null;

										return (
											<tr key={r.user?.guid || idx}>
												<td className="er-idx">{idx + 1}</td>
												<td>
													<span className="er-name">
														{r.user?.firstName} {r.user?.lastName}
													</span>
												</td>
												<td className="er-email">{r.user?.email || '—'}</td>
												<td className="er-country">{r.user?.country || '—'}</td>
												{isPaidEvent && (
													<td>
														{payment ? (
															<div className="er-payment-cell">
																{mode === 'virtual' && (
																	<span className="er-mode-badge er-mode-virtual">
																		<i className="fe fe-monitor me-1"></i>Virtual
																	</span>
																)}
																{mode === 'physical' && (
																	<span className="er-mode-badge er-mode-physical">
																		<i className="fe fe-map-pin me-1"></i>In-Person
																	</span>
																)}
																{mode === 'paid' && (
																	<span className="er-mode-badge er-mode-paid">
																		<i className="fe fe-check me-1"></i>Paid
																	</span>
																)}
																{payment.amount > 0 && (
																	<span className="er-amount">
																		{formatPrice(payment.amount)}
																	</span>
																)}
															</div>
														) : (
															<span className="er-unpaid">
																<i className="fe fe-clock me-1"></i>Unpaid
															</span>
														)}
													</td>
												)}
												<td className="er-date">
													{r.registeredAt
														? moment(r.registeredAt).format('MMM D, YYYY · h:mm A')
														: '—'}
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</div>
					</>
				)}
			</Modal.Body>

			<style>{`
				.er-modal-header {
					border-bottom: 2px solid #f1f5f9;
					padding: 1.25rem 1.5rem;
				}
				.er-modal-title {
					font-size: 1.0625rem;
					font-weight: 700;
					color: #1e293b;
					margin-bottom: 0.2rem;
				}
				.er-event-name { font-size: 0.8125rem; color: #94a3b8; }
				.er-loading, .er-empty {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					padding: 3rem 2rem;
					color: #94a3b8;
					font-size: 0.875rem;
				}
				.er-loading { flex-direction: row; }
				.er-count-bar {
					display: flex;
					align-items: center;
					gap: 0.75rem;
					padding: 0.75rem 1.5rem;
					background: #f8fafc;
					border-bottom: 1px solid #e2e8f0;
				}
				.er-count-badge {
					background: rgba(29,158,176,0.1) !important;
					color: #1d9eb0 !important;
					font-size: 0.75rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 8px;
					display: inline-flex;
					align-items: center;
				}
				.er-paid-badge {
					background: rgba(16,185,129,0.1) !important;
					color: #10b981 !important;
					font-size: 0.75rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 8px;
					display: inline-flex;
					align-items: center;
				}
				.er-capacity { font-size: 0.8125rem; color: #94a3b8; margin-left: auto; }
				.er-table-wrap { overflow-x: auto; }
				.er-table thead th {
					font-size: 0.75rem;
					font-weight: 700;
					color: #94a3b8;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					background: #f8fafc;
					border-bottom: 2px solid #e2e8f0;
					padding: 0.75rem 1rem;
					white-space: nowrap;
				}
				.er-table tbody td {
					padding: 0.75rem 1rem;
					vertical-align: middle;
					border-color: #f1f5f9;
				}
				.er-idx { color: #cbd5e1; font-size: 0.8125rem; width: 2.5rem; }
				.er-name { font-weight: 600; font-size: 0.875rem; color: #1e293b; }
				.er-email { font-size: 0.8125rem; color: #64748b; }
				.er-country { font-size: 0.8125rem; color: #64748b; }
				.er-date { font-size: 0.8125rem; color: #94a3b8; white-space: nowrap; }

				/* Payment column */
				.er-payment-cell { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
				.er-mode-badge {
					display: inline-flex;
					align-items: center;
					font-size: 0.75rem;
					font-weight: 700;
					padding: 3px 9px;
					border-radius: 6px;
					white-space: nowrap;
				}
				.er-mode-virtual {
					background: rgba(16,185,129,0.1);
					color: #10b981;
				}
				.er-mode-physical {
					background: rgba(246,133,33,0.1);
					color: #F68521;
				}
				.er-mode-paid {
					background: rgba(29,158,176,0.1);
					color: #1d9eb0;
				}
				.er-amount {
					font-size: 0.8125rem;
					font-weight: 600;
					color: #475569;
				}
				.er-unpaid {
					display: inline-flex;
					align-items: center;
					font-size: 0.75rem;
					font-weight: 600;
					color: #94a3b8;
				}
			`}</style>
		</Modal>
	);
};

export default EventRegistrantsModal;
